"use client";

import type { LessonDetails } from "@/features/workspaces/types/lessonDetails.ts";

import { Link, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { Calendar, Clock, Video, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths.ts";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import i18n from "@/i18n/config.ts";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions.tsx";
import {
  canCancelLesson,
  getCardStyles,
  getStatusBadgeText,
  getTimeUntilLesson,
} from "@/features/workspaces/utils/lessonUtils.ts";
import useUserStore from "@/features/auth/store/useUserStore";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface LessonDetailsCardProps {
  lessonDetails: LessonDetails;
  setCancelDialogOpen: (open: boolean) => void;
  workspaceId?: string;
}

export const LessonDetailsCard = ({
  lessonDetails,
  setCancelDialogOpen,
}: LessonDetailsCardProps) => {
  const { t } = useTranslation(["workspaces", "classroom", "common"]);
  const { workspaceId } = useParams();
  const hostDisplay = useHostingOptions().find((host) => lessonDetails.lesson_host === host.id);
  const { user } = useUserStore();
  const canCancel = canCancelLesson(lessonDetails, user?.timezone ?? null);

  // Parse lesson times - they come from server already in user's timezone
  const lessonStartTime = new Date(lessonDetails.start_time.replace(" ", "T"));
  const lessonEndTime = new Date(lessonDetails.end_time.replace(" ", "T"));

  // Compute local/derived lesson status based on current time window
  const nowZoned = toZonedTime(new Date(), user?.timezone ?? "UTC");
  const hasStarted = nowZoned.getTime() >= lessonStartTime.getTime();
  const hasEnded = nowZoned.getTime() >= lessonEndTime.getTime();
  const isOngoingTimeWindow = hasStarted && !hasEnded;

  const IN_PROGRESS_ID = "01F8MECHZX3TBDSZ7K9QDYFFJC";
  const effectiveStatusId = isOngoingTimeWindow ? IN_PROGRESS_ID : lessonDetails.status.id;
  const cardStyles = getCardStyles(effectiveStatusId);
  const canJoin = lessonDetails?.status?.id === IN_PROGRESS_ID || isOngoingTimeWindow;

  const joinUrl = useMemo(() => {
    if (lessonDetails.lesson_host === "other_way") {
      return null;
    }

    if (lessonDetails.generated_video_room_url) {
      return lessonDetails.generated_video_room_url;
    }

    const basePath =
      user?.role === "tutor"
        ? TUTOR_WORKSPACE_PATHS(workspaceId).LESSON
        : STUDENT_WORKSPACE_PATHS(workspaceId).LESSON;

    return `${basePath}/${lessonDetails.id}`;
  }, [
    lessonDetails.generated_video_room_url,
    lessonDetails.id,
    lessonDetails.lesson_host,
    user?.role,
    workspaceId,
  ]);

  const showVideoRoomError = Boolean(
    lessonDetails.video_room_data?.error && lessonDetails.video_room_data?.error_message,
  );

  // Dynamic time until lesson with minute updates
  const [dynamicTimeUntilLesson, setDynamicTimeUntilLesson] = useState<string | null>(
    getTimeUntilLesson(lessonDetails, i18n.language, user?.timezone ?? null),
  );

  useEffect(() => {
    // Update immediately
    setDynamicTimeUntilLesson(
      getTimeUntilLesson(lessonDetails, i18n.language, user?.timezone ?? null),
    );

    // Update every minute
    const interval = setInterval(() => {
      setDynamicTimeUntilLesson(
        getTimeUntilLesson(lessonDetails, i18n.language, user?.timezone ?? null),
      );
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [lessonDetails, user?.timezone]);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="col-span-12 lg:col-span-7"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4 }}
    >
      <div className="bg-side-accent/50 text-accent-foreground relative overflow-hidden rounded-2xl border-none p-4 shadow-2xl sm:p-6 lg:p-8">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
          <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-green-400 to-blue-500" />
        </div>

        <div className="relative">
          {/* Header Section */}
          <div className="mb-6 flex-grow-0 lg:mb-8">
            <div className="mb-4 flex items-center gap-4 sm:mb-6">
              <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                <div className="bg-muted/20 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl backdrop-blur-sm sm:h-16 sm:w-16 sm:rounded-2xl">
                  <Icon
                    className="text-accent-foreground"
                    height="1.5rem"
                    icon={lessonDetails.subject.icon}
                    width="1.5rem"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <h1 className="text-accent-foreground cursor-pointer truncate text-2xl leading-tight font-bold sm:text-3xl lg:text-4xl">
                        {lessonDetails.topic || t("workspaces:untitledLesson")}
                      </h1>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-accent-foreground text-base font-semibold">
                        {lessonDetails.topic || t("workspaces:untitledLesson")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <Badge
                className={`rounded-full px-3 py-1.5 text-xs font-medium sm:px-4 sm:py-2 sm:text-sm ${cardStyles.badge} flex-shrink-0 whitespace-nowrap`}
              >
                {getStatusBadgeText(effectiveStatusId, t, lessonDetails.status.name)}
              </Badge>
            </div>
          </div>

          {/* Details Grid - Date and Time */}
          <div className="mb-6 grid flex-grow grid-cols-1 gap-4 sm:mb-8 sm:grid-cols-2 sm:gap-6">
            {/* Date */}
            <div className="bg-muted/20 flex items-center gap-3 rounded-xl p-3 backdrop-blur-sm sm:gap-4 sm:p-4">
              <div className="bg-muted/30 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12">
                <Calendar className="text-muted-foreground h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-xs font-medium sm:text-sm">
                  {t("common:date", "Date")}
                </p>
                <p className="text-accent-foreground truncate text-sm font-semibold sm:text-base">
                  {format(lessonStartTime, "PPP", {
                    locale: getDateFnsLocale(i18n.language),
                  })}
                </p>
              </div>
            </div>

            {/* Time */}
            <div className="bg-muted/20 flex items-center gap-3 rounded-xl p-3 backdrop-blur-sm sm:gap-4 sm:p-4">
              <div className="bg-muted/30 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl sm:h-12 sm:w-12">
                <Clock className="text-muted-foreground h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-xs font-medium sm:text-sm">
                  {t("common:time", "Time")}
                </p>
                <p className="text-accent-foreground text-sm font-semibold sm:text-base">
                  {format(lessonStartTime, "p", {
                    locale: getDateFnsLocale(i18n.language),
                  })}{" "}
                  -{" "}
                  {format(lessonEndTime, "p", {
                    locale: getDateFnsLocale(i18n.language),
                  })}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6 grid flex-grow grid-cols-1 gap-4 sm:mb-8 sm:grid-cols-2 sm:gap-6">
            <div className="bg-muted/20 flex items-center gap-3 rounded-xl p-3 backdrop-blur-sm sm:gap-4 sm:p-4">
              <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-xl sm:h-12 sm:w-12">
                <span className={"flex size-5 items-center justify-center sm:size-6"}>
                  {hostDisplay?.icon}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-xs font-medium sm:text-sm">
                  {t("workspaces:lessonHost")}
                </p>
                <p className="text-accent-foreground text-sm font-semibold sm:text-base">
                  {hostDisplay?.title}
                </p>
              </div>
            </div>
            <div className="bg-muted/20 flex items-center gap-3 rounded-xl p-3 backdrop-blur-sm sm:gap-4 sm:p-4">
              <div className="bg-muted/30 flex h-10 w-10 items-center justify-center rounded-xl sm:h-12 sm:w-12">
                <Icon
                  className={"size-5 sm:size-6"}
                  height={"100%"}
                  icon={lessonDetails.subject.icon}
                  width={"100%"}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-xs font-medium sm:text-sm">
                  {t("workspaces:subject")}
                </p>
                <p className="text-accent-foreground text-sm font-semibold sm:text-base">
                  {lessonDetails.subject.name}
                </p>
              </div>
            </div>
          </div>

          {/* Time Until Lesson */}
          {dynamicTimeUntilLesson && (
            <div className="bg-muted/20 mb-6 flex-grow-0 rounded-xl p-3 backdrop-blur-sm sm:mb-8 sm:p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-500/20 sm:h-10 sm:w-10">
                  <Icon
                    className="text-blue-400"
                    height="1.25rem"
                    icon="mdi:timer-outline"
                    width="1.25rem"
                  />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs font-medium sm:text-sm">
                    {t("workspaces:startsIn", "Starts in")}
                  </p>
                  <p className="text-accent-foreground text-base font-bold sm:text-lg">
                    {dynamicTimeUntilLesson}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-auto flex flex-grow-0 flex-col gap-3 sm:flex-row sm:gap-4">
            {showVideoRoomError && (
              <div className="bg-destructive/10 text-destructive flex-1 rounded-xl px-4 py-3 text-sm sm:px-6 sm:py-4 sm:text-base">
                {lessonDetails.video_room_data?.error_message}
              </div>
            )}
            {!showVideoRoomError &&
              canJoin &&
              lessonDetails.lesson_host === "other_way" &&
              lessonDetails.communication_details && (
                <div className="bg-muted/20 text-accent-foreground flex-1 rounded-xl px-4 py-3 text-sm backdrop-blur-sm sm:px-6 sm:py-4 sm:text-base">
                  {lessonDetails.communication_details}
                </div>
              )}
            {!showVideoRoomError &&
              canJoin &&
              lessonDetails.lesson_host !== "other_way" &&
              joinUrl && (
                <Button
                  asChild
                  className="bg-accent-foreground/90 hover:bg-accent-foreground text-background flex-1 rounded-xl px-4 py-2.5 text-sm sm:px-6 sm:py-3 sm:text-base"
                  size="lg"
                >
                  <Link
                    rel={lessonDetails.generated_video_room_url ? "noreferrer" : undefined}
                    target={lessonDetails.generated_video_room_url ? "_blank" : undefined}
                    to={joinUrl}
                  >
                    <Video className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    {lessonDetails.generated_video_room_url
                      ? t("classroom:openVideoRoom", "Open video room")
                      : t("classroom:joinMeeting")}
                  </Link>
                </Button>
              )}
            {canCancel && (
              <Button
                className="flex-1 rounded-xl border-red-500/30 bg-red-500/20 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/30 sm:px-6 sm:py-3 sm:text-base"
                size="lg"
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
              >
                <X className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                {t("workspaces:cancelLesson")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
